import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import {
  DEFAULT_LOCAL,
  LOCAL_PROFILES,
  normalizeLocalParam,
  resolveLocalCode,
  SUPPORTED_LOCALS,
} from './locales';

const LocalizationContext = createContext({
  local: DEFAULT_LOCAL,
  profile: LOCAL_PROFILES[DEFAULT_LOCAL],
  loading: true,
  detectedFrom: 'default',
  setLocal: () => {},
  supported: SUPPORTED_LOCALS,
});

export const useLocalization = () => useContext(LocalizationContext);

const mergeProfile = (code, apiData = null) => {
  const base = LOCAL_PROFILES[code] || LOCAL_PROFILES[DEFAULT_LOCAL];
  if (!apiData) return base;
  return {
    ...base,
    ...apiData,
    // Keep marketing fields from the static profile (hero, mocks, testimonials).
    heroRegion: base.heroRegion,
    demonym: base.demonym,
    city: base.city,
    bookingBusiness: base.bookingBusiness,
    bookingServices: base.bookingServices,
    paymentExample: base.paymentExample,
    staffNames: base.staffNames,
    testimonials: base.testimonials,
    mobileMoneyLabel: base.mobileMoneyLabel,
    currency: { ...base.currency, ...(apiData.currency || {}) },
  };
};

// Reads ?local=KE (also accepts ?locale= / ?country=).
// Outside NG / GH / KE (or undetected) → US with USD, no country in the hero.
export const LocalizationProvider = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const paramValue =
    searchParams.get('local') || searchParams.get('locale') || searchParams.get('country');
  const paramLocal = normalizeLocalParam(paramValue);

  const [local, setLocalState] = useState(paramLocal || DEFAULT_LOCAL);
  const [profile, setProfile] = useState(LOCAL_PROFILES[paramLocal || DEFAULT_LOCAL]);
  const [detectedFrom, setDetectedFrom] = useState(paramLocal ? 'query' : 'default');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);

      const browserTimezone =
        typeof Intl !== 'undefined'
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : null;

      if (paramLocal) {
        const result = await api.getLocalization(paramLocal, {
          timezone: browserTimezone,
        });
        if (cancelled) return;

        const code = resolveLocalCode(paramLocal);
        if (result.data && !result.error) {
          setProfile(mergeProfile(code, result.data));
          setDetectedFrom(result.data.detectedFrom || 'query');
        } else {
          setProfile(mergeProfile(code));
          setDetectedFrom('query');
        }
        setLocalState(code);
        setLoading(false);
        return;
      }

      // No URL override — detect via API (headers → IP → timezone → language).
      // Anything outside NG/GH/KE becomes US.
      const result = await api.getLocalization(null, { timezone: browserTimezone });
      if (cancelled) return;

      const code = resolveLocalCode(result.data?.countryCode);
      if (result.data && !result.error) {
        setProfile(mergeProfile(code, result.data));
        setDetectedFrom(result.data.detectedFrom || 'default');
      } else {
        setProfile(mergeProfile(DEFAULT_LOCAL));
        setDetectedFrom('default');
      }
      setLocalState(code);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [paramLocal]);

  const setLocal = useCallback(
    (nextLocal) => {
      const code = resolveLocalCode(nextLocal);
      const next = new URLSearchParams(searchParams);
      next.set('local', code);
      next.delete('locale');
      next.delete('country');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const value = useMemo(
    () => ({
      local,
      profile,
      loading,
      detectedFrom,
      setLocal,
      supported: SUPPORTED_LOCALS,
    }),
    [local, profile, loading, detectedFrom, setLocal]
  );

  return (
    <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>
  );
};
